import { useContext, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import {
    CheckboxTableRow,
    ExTable,
    ModalButton,
    SelectionFixedFooter,
    TableBody,
    TableHead,
    TableWrapper,
    useMultipleCheckForm,
} from 'components/shared/widget'
import {
    RecollectForm,
    UpdateSamplePriorityForm,
    UpdateSampleStatusForm,
    useDownloadCsvSamplesApi,
    useFetchSamplesApi,
    usePostToExtractionApi,
    useRecollectSampleApi,
    useUpdateSamplePriorityApi,
    useUpdateSampleStatusApi,
} from 'api/sample'
import { Sample, SampleSearchForm } from 'entities/sample'
import { Pagination } from 'components/shared/pagination'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { SearchConditionView } from 'components/shared/search_drawer'
import { Alert, Button, Dropdown, Form } from 'react-bootstrap'
import { GlobalStateContext } from 'contexts/global_state_context'
import { sampleColumnInfoList, SampleTableData } from './sample_table'
import { DisplaySetting, DisplaySettingModal, TableHeads, useDisplaySetting } from 'components/shared/display_setting'
import qs from 'qs'
import { InputField, SelectField } from 'components/shared/input'
import { useState } from 'react'
import { MasterDataContext } from 'contexts/master_data_context'
import { TranscriptionForm } from 'components/shared/type'
import { TranscriptionModal } from 'components/shared/transcription_modal'
import { Flex } from 'components/shared/flex'
import { downloadFile } from 'utils/util'
import { useFetchProductItemsApi } from 'api/product_item'
import { ProductItemSearchForm } from 'entities/product_item'

type Props = {
    history: H.History
}

const SamplesPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const params = useQuery<SampleSearchForm>()
    const searchForm = useForm<SampleSearchForm>({
        sampleStatus: 'WAREHOUSED',
        ordering: '-sample_priority,sample_deadline_date',
        listSerializer: true,
        page: 1,
        perPage: 50,
        ...params,
    })
    const api = useFetchSamplesApi(searchForm)
    const downloadCsvApi = useDownloadCsvSamplesApi(searchForm)
    const checkIdForm = useMultipleCheckForm<Sample>()
    const toExtractionApi = usePostToExtractionApi()
    const productForm = useForm<ProductItemSearchForm>({ scope: 'all' })
    const productApi = useFetchProductItemsApi(productForm)
    const sampleDisplaySetting = useDisplaySetting(
        sampleColumnInfoList(
            searchForm,
            productApi.response.results.map((pi) => {
                return { label: pi.name, value: pi.id! }
            })
        ),
        'Sample'
    )
    const displaySettings: DisplaySetting[] = [sampleDisplaySetting]
    const [selectedSample, setSelectedSample] = useState<Sample>({})
    const [showEdit, setShowEdit] = useState<boolean>(false)
    const masterData = useContext(MasterDataContext)
    const transcriptionForm = useForm<TranscriptionForm>({ items: [] })
    const [showToExtModal, setShowToExtModal] = useState<boolean>(false)
    const [search, setSearch] = useState<string | undefined>()
    const updateStatusForm = useForm<UpdateSampleStatusForm>({})
    const updateStatusApi = useUpdateSampleStatusApi()
    const updatePriorityForm = useForm<UpdateSamplePriorityForm>({ priority: 0 })
    const updatePriorityApi = useUpdateSamplePriorityApi()
    const recollectForm = useForm<RecollectForm>({})
    const recollectApi = useRecollectSampleApi()

    useEffect(() => {
        api.execute()
        productApi.execute()
    }, [])

    const handleClickRemoveFilter = (key: keyof SampleSearchForm): void => {
        searchForm.update((f) => {
            delete f[key]
        })
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(toExtractionApi.loading)
        if (toExtractionApi.isSuccess()) {
            props.history.push('transcription_histories?type=EXTRACTION')
        }
    }, [toExtractionApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(downloadCsvApi.loading)
    }, [downloadCsvApi.loading])

    useEffect(() => {
        transcriptionForm.updateObject(
            'items',
            checkIdForm.form.object.items.map((item) => {
                return { id: item.code, count: 1 }
            })
        )
        updateStatusForm.updateObject(
            'items',
            checkIdForm.form.object.items.map((item) => {
                return { id: item.id }
            })
        )
        updatePriorityForm.updateObject(
            'items',
            checkIdForm.form.object.items.map((item) => {
                return { id: item.id }
            })
        )
    }, [checkIdForm.form.object])

    useEffect(() => {
        const queryParams = qs.stringify({ ...searchForm.object, page: api.pageSet.page, perPage: api.pageSet.perPage })
        if (props.history.location.search.replace('?', '') !== queryParams) {
            props.history.replace(`samples?${queryParams}`)
        }
    }, [searchForm.object])

    const handleClickEdit = (sample: Sample) => {
        setSelectedSample(sample)
        setShowEdit(true)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(downloadCsvApi.loading)
    }, [downloadCsvApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(updateStatusApi.loading)
        if (updateStatusApi.isSuccess()) {
            api.execute()
        }
    }, [updateStatusApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(updatePriorityApi.loading)
        if (updatePriorityApi.isSuccess()) {
            api.execute()
        }
    }, [updatePriorityApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(recollectApi.loading)
        if (recollectApi.isSuccess()) {
            api.execute()
        }
    }, [recollectApi.loading])

    return (
        <>
            <TranscriptionModal
                model="Sample"
                toModel="Extraction"
                show={showToExtModal}
                transcriptionForm={transcriptionForm}
                onClickOk={() => {
                    setShowToExtModal(false)
                    checkIdForm.form.updateObject('items', [])
                    toExtractionApi.execute(transcriptionForm)
                }}
                onHide={() => setShowToExtModal(false)}
                showResearchName
                // showUseUp
            />
            <ContentHeader
                headerTitle="検体"
                left={
                    <>
                        {/* {checkIdForm.object.ids.length === 1 && <Button variant="success" onClick={handleClickEdit}>検体を編集</Button>} */}
                        <Button
                            variant="success"
                            disabled={checkIdForm.form.object.items.length !== 0}
                            onClick={() => {
                                transcriptionForm.updateObject('isTest', true)
                                setShowToExtModal(true)
                            }}
                        >
                            標準尿のみ転記
                        </Button>

                        <SearchConditionView searchForm={searchForm} onClickRemoveButton={handleClickRemoveFilter} hiddenSearchParams={['sampleStatus']} />
                    </>
                }
                right={
                    <>
                        <Form.Group controlId="show-all">
                            <Form.Check
                                type="checkbox"
                                style={{ userSelect: 'none' }}
                                label={'すべて表示'}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        searchForm.update((f) => delete f.sampleStatus)
                                    } else {
                                        searchForm.update((f) => (f.sampleStatus = 'WAREHOUSED'))
                                    }
                                }}
                                checked={!searchForm.object.sampleStatus}
                            />
                        </Form.Group>
                        <DisplaySettingModal displaySettings={displaySettings} />
                    </>
                }
            />
            <Flex style={{ marginTop: 5 }} alignItems="center" justifyContent="space-between">
                <Flex alignItems="center">
                    <Form.Control
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value)
                        }}
                        placeholder="検体コード検索"
                        onKeyPress={(e) => {
                            if (e.which === 13) {
                                searchForm.updateObject('code', search)
                            }
                        }}
                        style={{ marginBottom: 0, width: 250 }}
                    />
                </Flex>
                <Flex alignItems="center">
                    <Pagination pageSet={api.pageSet} response={api.response} searchForm={searchForm}></Pagination>
                    <div style={{ minWidth: 140, textAlign: 'right' }}>総件数： {api.response.count.toLocaleString()}件</div>
                </Flex>
            </Flex>
            <ContentBody>
                <TableWrapper showFooter={checkIdForm.form.object.items.length > 0} contentHeaderCount={2}>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <CheckboxTableRow head allCheck allItems={api.response.results} multipleCheckForm={checkIdForm} showEdit>
                                <TableHeads displaySetting={sampleDisplaySetting} />
                            </CheckboxTableRow>
                        </TableHead>
                        <TableBody loading={api.loading} columnLength={sampleDisplaySetting.getColumnLength() + 1}>
                            {api.response.results.map((sample: Sample) => {
                                return (
                                    <CheckboxTableRow
                                        key={`sample-${sample.id}`}
                                        item={sample}
                                        multipleCheckForm={checkIdForm}
                                        onClickEdit={() => handleClickEdit(sample)}
                                    >
                                        <SampleTableData sample={sample} displaySetting={sampleDisplaySetting} />
                                    </CheckboxTableRow>
                                )
                            })}
                        </TableBody>
                    </ExTable>
                    <Pagination pageSet={api.pageSet} response={api.response} searchForm={searchForm}></Pagination>
                </TableWrapper>
            </ContentBody>

            <SelectionFixedFooter
                ids={checkIdForm.ids()}
                onClickReset={() => checkIdForm.form.resetForm()}
                searchForm={searchForm}
                searchAttr="sampleIdIn"
                tableName="Sample"
            >
                <Button
                    variant="success"
                    onClick={() => {
                        transcriptionForm.updateObject('isTest', false)
                        setShowToExtModal(true)
                    }}
                >
                    Extractionに転記
                </Button>
                <ModalButton
                    disabled={checkIdForm.form.object.items.some((s) => !['PCR_READY', 'DNA_SYNTHESIZED', 'RNA_EXTRACTED'].includes(s.status!))}
                    openButton={
                        <Button
                            variant="warning"
                            disabled={checkIdForm.form.object.items.some((s) => !['PCR_READY', 'DNA_SYNTHESIZED', 'RNA_EXTRACTED'].includes(s.status!))}
                        >
                            再合成/再抽出
                        </Button>
                    }
                    modalHeader={<>再抽出/再合成</>}
                    modalBody={
                        <div>
                            <SelectField
                                form={updateStatusForm}
                                attr="status"
                                label="アクション"
                                selectItems={[
                                    { label: '再抽出', value: 'WAREHOUSED' },
                                    { label: '再合成', value: 'RNA_EXTRACTED' },
                                ]}
                                includeBlank
                            />
                        </div>
                    }
                    submitButton={
                        <Button
                            onClick={() => {
                                globalState.showConfirm(
                                    <>
                                        <Alert variant="danger">管理者以外このアクションは実行しないでください</Alert>
                                        更新する場合は「更新」と入力してOKをクリックしてください
                                    </>,
                                    () => {
                                        updateStatusApi.execute(updateStatusForm)
                                    },
                                    { confirmWord: '更新' }
                                )
                            }}
                        >
                            更新
                        </Button>
                    }
                ></ModalButton>

                <ModalButton
                    openButton={<Button variant="info">最優先フラグ</Button>}
                    modalHeader={<>最優先フラグの編集</>}
                    modalBody={
                        <div>
                            <SelectField
                                form={updatePriorityForm}
                                attr="priority"
                                label="優先度"
                                selectItems={[
                                    { label: '普通', value: 0 },
                                    { label: '特急', value: 1 },
                                    { label: '超特急', value: 2 },
                                ]}
                            />
                        </div>
                    }
                    submitButton={
                        <Button
                            onClick={() => {
                                globalState.showConfirm(
                                    <>
                                        <Alert variant="danger">管理者以外このアクションは実行しないでください</Alert>
                                        更新する場合は「更新」と入力してOKをクリックしてください
                                    </>,
                                    () => {
                                        updatePriorityApi.execute(updatePriorityForm)
                                    },
                                    { confirmWord: '更新' }
                                )
                            }}
                        >
                            更新
                        </Button>
                    }
                ></ModalButton>

                {checkIdForm.form.object.items.length === 1 && (
                    <ModalButton
                        openButton={<Button variant="danger">再採尿</Button>}
                        modalHeader={<>再採尿</>}
                        modalBody={
                            <div>
                                <Alert>
                                    現在の依頼書コード: {checkIdForm.form.object.items[0].requestForm?.code}
                                    <br />
                                    {checkIdForm.form.object.items[0].rack?.locationCode} {checkIdForm.form.object.items[0].tubeLocation}の尿を廃棄してください
                                </Alert>
                                {!process.env.REACT_APP_IS_HOME_TEST && (
                                    <InputField form={recollectForm} attr="newRequestFormCode" label="医療機関の場合は新しい依頼書コードを入力してください" />
                                )}
                                <Alert variant="danger">管理者以外は実行しないでください</Alert>
                            </div>
                        }
                        submitButton={
                            <Button
                                variant="danger"
                                onClick={() => {
                                    console.log(checkIdForm.form.object.items[0])
                                    const sampleId = checkIdForm.form.object.items[0].id
                                    recollectApi.execute(Number(sampleId), recollectForm)
                                }}
                            >
                                再採尿
                            </Button>
                        }
                    ></ModalButton>
                )}
                <Dropdown>
                    <Dropdown.Toggle variant="outline-success" id="dropdown-basic">
                        ツール
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item
                            onClick={() => {
                                const csv = checkIdForm.form.object.items
                                    .sort((a, b) => {
                                        if (!a.tubeLocation) {
                                            return -1
                                        }
                                        if (!b.tubeLocation) {
                                            return -1
                                        }
                                        return a.tubeLocation < b.tubeLocation ? -1 : 1
                                    })
                                    .map((sample) => {
                                        return sample.code
                                    })
                                    .join('\n')
                                downloadFile(csv, 'misignal_sample_label_print.csv', 'text/csv')
                            }}
                        >
                            ラベル印刷用CSV出力
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </SelectionFixedFooter>
        </>
    )
}

export default withRouter(SamplesPage)
